<template>
  <div id="gridTimePicker" :class="['col-' + Math.ceil(60 / step)]">
    <template v-for="hour of hours">
      <div class="hour" v-for="n in Math.ceil(60 / step)" :class="{
        selected: selected.indexOf(formatHour(hour, n)) > -1,
        first: formatHour(hour, n) == firstSelected,
        last: formatHour(hour, n) == lastSelected,
        disabled: isDisabled(formatHour(hour, n)) || disableAll
        }"
        :key="'hour-' + hour + '-minute-' + n" @click="selectHour(formatHour(hour, n))">
        {{ formatHour(hour, n) }} - {{ formatNextHour(hour, n) }}
      </div>
    </template>
    <div class="none" v-if="times.length == 0">{{ $t("booking.none") }}</div>
  </div>
</template>

<script>

  import moment from 'moment';

  export default {

    name: "gridTimePicker",

    data() {
      return {
        start: "00:00",
        end: "24:00",
        step: 15,
        selected: [],
        disabled: []
      };
    },

    props: {
      times: Array,
      date: String,
      schedule: Object,
      min: Number,
      max: Number,
      isSelected: Boolean
    },

    mounted() {
      this.calculateTimes();
    },

    watch: {
      isSelected(newVal, oldVal) {
        if (!newVal) {
          this.selected = [];
        }
      },
      selected() {
        if (this.isSelected) {
          this.$emit("sizeChanged", this.getSelectedSize());
        }
      },
      date() {
        this.calculateTimes();
      },
      schedule() {
        this.calculateTimes();
      }
    },

    computed: {
      hours() {
        let start = moment(this.start, "H:mm").set("minutes", 0);
        let end = moment(this.end, "H:mm");
        let toReturn = [];
        while (start.isBefore(end)) {
          toReturn.push(start.get("hours"));
          start = start.add(this.hourStep, "hours");
        }
        return toReturn;
      },
      disableAll() {
        let minimumToSelect = Math.floor(this.min / this.step);
        return minimumToSelect > 0 && this.hours.filter(h => !this.isDisabled(this.formatHour(h, 1))).length < minimumToSelect;
      },
      hourStep() {
        return Math.max(Math.floor(this.step / 60), 1);
      },
      firstSelected() {
        if (this.selected.length > 0) {
          return this.selected[0];
        }
        return null;
      },
      lastSelected() {
        if (this.selected.length > 0) {
          return this.selected[this.selected.length - 1];
        }
        return null;
      }
    },

    methods: {
      leftPad(str, length) {
        return "0".repeat(Math.max(length - String(str).length, 0)) + String(str);
      },
      formatHour(hour, minuteIndex) {
        return this.leftPad(hour, 2) + ":" + this.formatMinutes(minuteIndex);
      },
      formatMinutes(minuteIndex) {
        return this.leftPad((minuteIndex - 1) * this.step, 2);
      },
      formatNextHour(hour, minuteIndex) {
        let nextMinutes = Number.parseInt(this.leftPad(minuteIndex * this.step, 2));
        let trueNextMinutes = nextMinutes % 60;
        if (nextMinutes >= 60) {
          let hoursToAdd = nextMinutes / 60;
          return this.leftPad(hour + hoursToAdd, 2) + ":" + this.leftPad(trueNextMinutes, 2);
        }
        return this.leftPad(hour, 2) + ":" + nextMinutes;
      },
      selectHour(hour, cascade = 0) {
        if (this.isDisabled(hour) || this.disableAll) {
          return false;
        }
        if (this.selected.indexOf(hour) == -1) { //Not yet selected
          // Cascade the next hours

          let minimumRequired = Math.floor(this.min / this.step);
          let currentlySelectedSize = this.selected.length;

          if (minimumRequired > 0 && currentlySelectedSize === 0 //A miminum is required and nothing is selected
            && (cascade + currentlySelectedSize < minimumRequired)) { //Not enough hours are selected to meet minimum requirement
            let selectedHourIndex = this.hours.indexOf(parseInt(hour.split(":")[0]));
            let selectHourMinutes = Number.parseInt(hour.split(":")[1]);
            if (selectedHourIndex < this.hours.length) {

              //Select minutes until next hour from selected time
              for (let n = 1; n <= Math.ceil(60 / this.step); n++) {
                let minutes = Number.parseInt(this.formatMinutes(n));
                if (minutes <= selectHourMinutes) { //Minutes before current minutes will be ignored
                  continue;
                }
                //Select minutes after current minutes
                this.selectHour(hour.split(":")[0] + ":" + this.formatMinutes(n), cascade + 1);
              }

              //Should select next hour if this is reached & cascade is still required
              let nextHour = this.formatHour(this.hours[selectedHourIndex + 1], 1);
              if (this.selected.indexOf(nextHour) == -1 && cascade + 1 < minimumRequired) {
                this.selectHour(nextHour, cascade + 1);
              }
            }
          }

          // Check if hour is valid
          if (this.selected.length > 0 && cascade == 0) {
            let valid = false;
            for (let selectedHour of this.selected) {
              if (hour == this.hourAfter(selectedHour) || hour == this.hourBefore(selectedHour)) { //hour should be immediately before or after next one
                valid = true;
                break;
              }
            }
            if (!valid) {
              if (this.selected.length == 1 && minimumRequired <= 1) {//If only one is selected, then reset selection and select new hour instead
                this.selected = [];
              } else { //stop selection
                return false;
              }
            }
          }

          //Select hour
          this.selected.push(hour);
          this.selected = this.selected.sort();
          this.$emit("selected");
          return true;
        } else {
          if (this.selected.length > 0 && hour != this.selected[0] && hour != this.selected[this.selected.length - 1]) {
            return false;
          }
          this.selected = this.selected.filter(h => h != hour);
          return true;
        }
        this.selected = this.selected.filter((e, index) => this.selected.indexOf(e) == index);
      },
      hourBefore(hour) {
        return moment(hour, "HH:mm").subtract(this.step, "minutes").format("HH:mm");
      },
      hourAfter(hour) {
        return moment(hour, "HH:mm").add(this.step, "minutes").format("HH:mm");
      },
      isDisabled(hour) {
        let beforeNow = moment.utc(this.date).format("YYYY-MM-DD") == moment().format("YYYY-MM-DD") && hour < moment().format("HH:mm");
        return this.disabled.indexOf(hour) > -1 || hour < this.start || hour >= this.end || beforeNow;
      },
      getSelectedSize() {
        return this.selected.length;
      },
      getSelected() {
        if (this.selected.length == 0) {
          return "none";
        }
        let first = this.selected[0];
        let last = this.selected[this.selected.length - 1];
        let start = moment.utc(this.date).set("hours", 0).set("minutes", 0).set("seconds", 0).set("milliseconds", 0)
          .add(first.split(":")[0], "hours").add(first.split(":")[1], "minutes");
        let end = moment.utc(this.date).set("hours", 0).set("minutes", 0).set("seconds", 0).set("milliseconds", 0)
          .add(last.split(":")[0], "hours").add(last.split(":")[1], "minutes").add(this.step, "minutes");
        let duration = moment.duration(end.diff(start));
        let minutes = duration.asMinutes();
        if (this.schedule.minimumDuration && minutes < this.schedule.minimumDuration) {
          if (this.schedule.minimumDuration === this.schedule.maximumDuration) {
            return "minimumInvalidDuration";
          }
          return "minimumDuration";
        } else if (this.schedule.maximumDuration && minutes > this.schedule.maximumDuration) {
          if (this.schedule.minimumDuration === this.schedule.maximumDuration) {
            return "maximumInvalidDuration";
          }
          return "maximumDuration";
        }
        return {start, end};
      },
      calculateTimes() {
        if (!this.times) {
          return;
        }
        this.disabled = [];
        this.selected = [];
        this.step = this.schedule ? this.schedule.step : 15;
        let buffer = this.schedule ? this.schedule.bufferDuration : 30;

        // Generate start and end of calendar
        let bestStart = "24:00";
        let bestEnd = "00:00";
        for (let time of this.times) {
          if (time.from < bestStart) {
            bestStart = time.from;
          }
          if (time.to > bestEnd) {
            bestEnd = time.to;
          }
        }
        this.start = bestStart;
        this.end = bestEnd;

        this.optimizeSteps();

        // Disable all times between the times received
        if (this.times.length > 1) {
          for (let i = 0; i < this.times.length - 1; i++) {
            let start = moment(this.times[i].to, "HH:mm").subtract(buffer, "minutes");
            let end = moment(this.times[i + 1].from, "HH:mm").add(buffer, "minutes");
            while (start.isBefore(end)) {
              this.disabled.push(start.format("HH:mm"));
              start.add(this.step, "minutes");
            }
          }
        }
      },
      clearSelected() {
        this.selected = [];
      },
      optimizeSteps() {
        let startTime = this.convertTimeToMinutes(this.start);
        let endTime = this.convertTimeToMinutes(this.end);
        let duration = endTime - startTime;
        if (this.min == this.max && this.min > 0 && (duration % this.min) === 0) {
          this.step = this.min;
        }
      },

      convertTimeToMinutes(time) {
        return Number.parseInt(time.substring(0,2)) * 60 + Number.parseInt(time.substring(3))
      }
    }

  }

</script>

<style lang="scss" scoped>

  #gridTimePicker {
    width: 100%;
    display: grid;
    grid-gap: 0;
    padding-right: 2px;
    padding-bottom: 2px;
    &.col-1 {
      grid-template-columns: 1fr;
      .hour.selected {
        border-left: 2px solid #405159;
        border-right: 2px solid #405159;
      }
    }
    &.col-2 {
      grid-template-columns: 1fr 1fr;
      .hour.selected.first, .hour.selected:nth-child(2n + 1) {
        border-left: 2px solid #405159;
      }
      .hour.selected.last, .hour.selected:nth-child(2n) {
        border-right: 2px solid #405159;
      }
    }
    &.col-4 {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      .hour.selected.first, .hour.selected:nth-child(4n + 1) {
        border-left: 2px solid #405159;
      }
      .hour.selected.last, .hour.selected:nth-child(4n) {
        border-right: 2px solid #405159;
      }
    }
  }

  .hour-title {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 14px 20px 13px 0;
  }

  .hour {
    text-align: center;
    padding: 13px 0;
    font-weight: bold;
    color: #001722;
    background-color: #e1e4e6;
    border: 2px solid transparent;
    margin-bottom: -2px;
    margin-right: -2px;
    user-select: none;
    cursor: pointer;
    &.selected {
      position: relative;
      &::before, &::after {
        content: "";
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        height: 2px;
        background-color: #405159;
      }
      &::after {
        top: auto;
        bottom: -2px;
      }
      border-top: 2px solid #405159;
      border-bottom: 2px solid #405159;
    }
    &.disabled {
      color: #bfc5c8;
      background-color: transparent;
      border-color: transparent;
    }
  }

  .none {
    grid-column: span 5;
    text-align: center;
    font-weight: bold;
  }

  .desktop {
    .hour {
      padding: 10px 0;
      font-size: 14px;
    }
  }

</style>
